/*
 * D2D Vehicle Filter
 * ------------------
 * A cascading Make > Model > Sub-Model selector that drives Globo Smart
 * Product Filter & Search. The dropdowns hold no filter state of their own —
 * they are a remote control for Globo's checkboxes, and Globo is the single
 * source of truth:
 *
 *   - Selecting a value clicks the matching hidden Globo checkbox (instant
 *     apply, no submit button). Changing a level first clears every level below
 *     it so selections replace rather than stack.
 *   - A "reconcile" step reads Globo's currently-checked vehicle checkboxes and
 *     mirrors them back into the dropdowns. This keeps the dropdowns in sync
 *     when filters change OUTSIDE of them — Globo's "Clear All", removing an
 *     active-filter pill, or a deep-linked URL with filters already applied.
 *
 * The panel relocates itself to the top of Globo's sidebar (#gf-tree). Globo
 * re-renders that tree on every AJAX filter change, so a MutationObserver
 * re-injects the same panel node. Because the displayed state is always derived
 * from Globo, a re-render can never desync us — we just rebuild from the truth.
 *
 * Globo's native Make / Model / Sub-Model blocks are hidden via CSS (see
 * d2d-vehicle-filter.liquid); the dropdowns replace them. The active-filter
 * pills stay visible so a shopper can remove a level directly.
 */
(function () {
  'use strict';

  var GF_TREE_ID = 'gf-tree';
  var stripPrefix = function (value) {
    return value.replace(/^(make_|model_|submodel_)/, '');
  };

  document.addEventListener('DOMContentLoaded', function () {
    var panel = document.getElementById('d2dVehiclePanel');
    if (!panel || panel.dataset.d2dReady === 'true') return;
    panel.dataset.d2dReady = 'true';

    var makeSelect  = panel.querySelector('#d2dMakeSelect');
    var modelSelect = panel.querySelector('#d2dModelSelect');
    var subSelect   = panel.querySelector('#d2dSubModelSelect');
    var clearLink   = panel.querySelector('#d2dClearVehicle');
    if (!makeSelect || !modelSelect || !subSelect) return;

    var hierarchyUrl = window.d2dVehicleHierarchyUrl;
    if (!hierarchyUrl) {
      console.error('D2D Vehicle Filter: window.d2dVehicleHierarchyUrl is not set.');
      return;
    }

    // Page context: on a vehicle collection page the custom.vehicle_make /
    // vehicle_model metafields (emitted by the Liquid section) lock those
    // levels. Locked levels are page context, NOT Globo filters — the
    // collection already scopes the products, so we never click a Globo
    // checkbox for them. On any non-vehicle page these are '' and the selector
    // behaves exactly as it does everywhere else.
    var lockedMake  = window.d2dLockedMake  || '';
    var lockedModel = window.d2dLockedModel || '';

    // --- Globo DOM helpers -------------------------------------------------

    var gfButtons = function () {
      var tree = document.getElementById(GF_TREE_ID);
      return tree ? tree.querySelectorAll('button[role="checkbox"][data-fvalue]') : [];
    };

    var isChecked = function (button) {
      return button.getAttribute('aria-checked') === 'true' ||
             button.classList.contains('checked');
    };

    // Match on the exact data-fvalue string (getAttribute, not a CSS selector)
    // so values containing slashes or other special characters always resolve.
    var findGloboButton = function (value) {
      var buttons = gfButtons();
      for (var i = 0; i < buttons.length; i++) {
        if (buttons[i].getAttribute('data-fvalue') === value) return buttons[i];
      }
      return null;
    };

    // The currently-checked value at a prefix level (e.g. 'model_'), or ''.
    var getChecked = function (prefix) {
      var buttons = gfButtons();
      for (var i = 0; i < buttons.length; i++) {
        var val = buttons[i].getAttribute('data-fvalue');
        if (val && val.indexOf(prefix) === 0 && isChecked(buttons[i])) return val;
      }
      return '';
    };

    // The set of values Globo currently renders a checkbox for at a prefix
    // level — i.e. vehicles that actually have products. Returns null when
    // Globo's tree hasn't rendered yet, so callers can fall back to the full
    // hierarchy instead of showing an empty dropdown during load.
    var availableSet = function (prefix) {
      var tree = document.getElementById(GF_TREE_ID);
      if (!tree) return null;
      var set = {};
      var buttons = tree.querySelectorAll('button[role="checkbox"][data-fvalue]');
      for (var i = 0; i < buttons.length; i++) {
        var val = buttons[i].getAttribute('data-fvalue');
        if (val && val.indexOf(prefix) === 0) set[val] = true;
      }
      return set;
    };

    // Narrow a list of hierarchy values to those Globo has products for. Until
    // Globo is ready (null set), return the full list so a dropdown is never
    // empty during load.
    var filterAvailable = function (values, prefix) {
      var set = availableSet(prefix);
      if (!set) return values;
      return values.filter(function (v) { return set[v]; });
    };

    // Sorted available values for a prefix — used in the reconcile signature so
    // the dropdowns rebuild when Globo's option lists change (e.g. its tree
    // finishing its initial render).
    var availableList = function (prefix) {
      var set = availableSet(prefix);
      return set ? Object.keys(set).sort() : [];
    };

    // Apply a single tag value — clicks its Globo checkbox if not already on.
    var applyValue = function (value) {
      var button = findGloboButton(value);
      if (!button) {
        console.warn('D2D Vehicle Filter: no Globo option for "' + value +
          '". It may have no products in this collection, or the tag is not indexed.');
        return;
      }
      if (!isChecked(button)) button.click();
    };

    // Clear every currently-applied value at a given prefix level.
    var clearLevel = function (prefix) {
      var buttons = gfButtons();
      for (var i = 0; i < buttons.length; i++) {
        var val = buttons[i].getAttribute('data-fvalue');
        if (val && val.indexOf(prefix) === 0 && isChecked(buttons[i])) buttons[i].click();
      }
    };

    // --- Dropdown rendering ------------------------------------------------

    var resetSelect = function (select, placeholder) {
      select.innerHTML = '<option value="">' + placeholder + '</option>';
    };

    var addOptions = function (select, values) {
      values.slice().sort().forEach(function (value) {
        var option = document.createElement('option');
        option.value = value;
        option.textContent = stripPrefix(value);
        select.appendChild(option);
      });
    };

    // Hierarchy lists, each narrowed to vehicles Globo actually has products
    // for. The JSON gives the cascade structure; Globo gives live availability.
    var listMakes = function (data) {
      return filterAvailable(Object.keys(data), 'make_');
    };

    var listModels = function (data, make) {
      return filterAvailable(make ? Object.keys(data[make] || {}) : [], 'model_');
    };

    var listSubModels = function (data, make, model) {
      var subs = (make && model && data[make]) ? (data[make][model] || []) : [];
      return filterAvailable(subs, 'submodel_');
    };

    var ensureOption = function (select, value) {
      if (!value) return;
      for (var i = 0; i < select.options.length; i++) {
        if (select.options[i].value === value) return;
      }
      var o = document.createElement('option');
      o.value = value;
      o.textContent = stripPrefix(value);
      select.appendChild(o);
    };

    // Lock a level fixed by the collection's metafields: force the value,
    // guarantee the option exists (even if Globo has no checkbox for it), and
    // disable the control so it can't be changed.
    var applyLock = function (select, value) {
      if (!value) return;
      ensureOption(select, value);
      select.value = value;
      select.disabled = true;
    };

    // Render all three dropdowns for a given vehicle state. Rebuilds the option
    // lists (so availability is always current) and sets values without
    // dispatching 'change', so this never re-triggers the apply handlers.
    var setDropdowns = function (data, make, model, submodel) {
      resetSelect(makeSelect, 'Select Make');
      addOptions(makeSelect, listMakes(data));
      makeSelect.value = make || '';
      makeSelect.disabled = false;

      resetSelect(modelSelect, 'Select Model');
      addOptions(modelSelect, listModels(data, make));
      modelSelect.value = model || '';
      modelSelect.disabled = !make || modelSelect.options.length <= 1;

      resetSelect(subSelect, 'Select Sub-Model');
      addOptions(subSelect, listSubModels(data, make, model));
      subSelect.value = submodel || '';
      subSelect.disabled = !model || subSelect.options.length <= 1;

      // Page-context locks override make / model.
      applyLock(makeSelect, lockedMake);
      applyLock(modelSelect, lockedModel);

      // "Clear vehicle" is only relevant when there's an unlocked selection.
      if (clearLink) {
        var hasUnlocked = (!lockedMake && !!makeSelect.value) ||
                          (!lockedModel && !!modelSelect.value) ||
                          !!subSelect.value;
        clearLink.hidden = !hasUnlocked;
      }
    };

    // --- Reconcile: mirror Globo's checked state into the dropdowns ---------

    // While the dropdowns are driving Globo, suppress hierarchy enforcement so
    // reconcile doesn't second-guess our own intermediate clear/apply clicks.
    var selfDriveUntil = 0;
    var markSelfDrive = function () { selfDriveUntil = Date.now() + 800; };

    var lastSig = null;
    var reconcile = function (data) {
      // Locked levels come from page context, not Globo. Treating them as
      // present means the orphan check never clears a child whose parent is
      // locked (the collection is the parent), and the dropdowns keep showing
      // the locked vehicle.
      var mk = lockedMake  || getChecked('make_');
      var md = lockedModel || getChecked('model_');
      var sm = getChecked('submodel_');
      // Signature covers both checked state and availability, so the dropdowns
      // rebuild when filters change AND when Globo's option lists change (e.g.
      // its tree finishing its initial render and narrowing the make list).
      var sig = [mk, md, sm,
        availableList('make_').join(','),
        availableList('model_').join(','),
        availableList('submodel_').join(',')].join('|');
      if (sig === lastSig) return;       // nothing vehicle-related changed
      lastSig = sig;                     // set before acting: blocks re-entry on the same state

      // Enforce the hierarchy: a child can't stay applied without its parent.
      // When a parent is removed externally (a pill, Clear All), clear the
      // orphaned children in Globo; the resulting re-render re-fires reconcile
      // with a clean state, which then updates the dropdowns.
      if (Date.now() >= selfDriveUntil) {
        if (!mk && (md || sm)) { clearLevel('model_'); clearLevel('submodel_'); return; }
        if (!md && sm) { clearLevel('submodel_'); return; }
      }

      setDropdowns(data, mk, md, sm);
    };

    // --- Apply handlers (dropdown -> Globo) --------------------------------

    var wire = function (data) {
      makeSelect.addEventListener('change', function () {
        markSelfDrive();
        var make = makeSelect.value;
        clearLevel('submodel_');
        clearLevel('model_');
        clearLevel('make_');
        if (make) applyValue(make);
        setDropdowns(data, make, '', '');   // optimistic; reconcile confirms
      });

      modelSelect.addEventListener('change', function () {
        markSelfDrive();
        var make = makeSelect.value;
        var model = modelSelect.value;
        clearLevel('submodel_');
        clearLevel('model_');
        if (model) applyValue(model);
        setDropdowns(data, make, model, '');
      });

      subSelect.addEventListener('change', function () {
        markSelfDrive();
        var make = makeSelect.value;
        var model = modelSelect.value;
        var sub = subSelect.value;
        clearLevel('submodel_');
        if (sub) applyValue(sub);
        setDropdowns(data, make, model, sub);
      });

      if (clearLink) {
        clearLink.addEventListener('click', function (event) {
          event.preventDefault();
          markSelfDrive();
          clearLevel('submodel_');
          if (!lockedModel) clearLevel('model_');
          if (!lockedMake) clearLevel('make_');
          setDropdowns(data, lockedMake || '', lockedModel || '', '');
        });
      }
    };

    // --- Relocation into Globo's sidebar -----------------------------------

    // Insert the panel at the top of Globo's filter list (inside #gf-tree, just
    // under its header) so it sits in the sidebar at the correct column width.
    // Idempotent — only moves the node when it isn't already in place, so
    // repeated calls settle without looping.
    var ensureInjected = function () {
      var tree = document.getElementById(GF_TREE_ID);
      if (!tree) return false;
      var target = tree.querySelector('.gf-filter-contents') || tree;
      if (target.firstElementChild !== panel) target.insertBefore(panel, target.firstElementChild);
      panel.hidden = false;
      return true;
    };

    var setup = function (data) {
      // Globo re-renders #gf-tree on every AJAX apply, so re-inject the panel
      // and reconcile the dropdowns whenever the DOM changes.
      var scheduled = false;
      var observer = new MutationObserver(function () {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(function () {
          scheduled = false;
          ensureInjected();
          reconcile(data);
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });

      ensureInjected();
      reconcile(data);   // reflect any filters already applied (e.g. deep link)
    };

    // --- Boot --------------------------------------------------------------

    fetch(hierarchyUrl)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        // If only the model is locked, derive its make from the hierarchy.
        if (lockedModel && !lockedMake) {
          Object.keys(data).some(function (mk) {
            if (data[mk] && data[mk][lockedModel] !== undefined) { lockedMake = mk; return true; }
            return false;
          });
        }
        setDropdowns(data, lockedMake || '', lockedModel || '', '');
        wire(data);
        setup(data);
      })
      .catch(function (err) {
        console.error('D2D Vehicle Filter: failed to load vehicle-hierarchy.json', err);
      });
  });
})();
